import { useState, useEffect, useCallback } from "react";
import { OnboardingContext } from "@/hooks/useOnboarding";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { X, ChevronLeft, ChevronRight, Share, Download, Smartphone, Monitor, User } from "lucide-react";
import { safeGetItem } from "@/lib/safeStorage";
import { useAuth } from "@/_core/hooks/useAuth";

interface TutorialStep {
  title: string;
  description: string;
  content: React.ReactNode;
  icon?: React.ReactNode;
}

export default function OnboardingTutorial({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const [isOpenState, setIsOpenState] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [platform, setPlatform] = useState<"ios" | "android" | "desktop">("desktop");

  useEffect(() => {
    // Detect platform
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    const isAndroid = /android/.test(userAgent);
    
    if (isIOS) {
      setPlatform("ios");
    } else if (isAndroid) {
      setPlatform("android");
    } else {
      setPlatform("desktop");
    }

    // Show tutorial on EVERY visit (persistent onboarding)
    // This ensures users always see PWA installation instructions
    setTimeout(() => setIsOpenState(true), 1000);
  }, []);

  const openTutorial = useCallback(() => {
    setIsOpenState(true);
    setCurrentStep(0);
  }, []);

  const getPWAInstallContent = () => {
    if (platform === "ios") {
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <Smartphone className="w-6 h-6 text-blue-400" />
            <div>
              <p className="font-semibold text-blue-400">iOS Installation</p>
              <p className="text-sm text-muted-foreground">Install as app for best experience</p>
            </div>
          </div>
          
          <ol className="space-y-3 text-sm">
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-bold">1</span>
              <div>
                <p className="font-semibold">Tap the Share button</p>
                <div className="flex items-center gap-2 mt-1 text-muted-foreground">
                  <Share className="w-4 h-4" />
                  <span>at the bottom of Safari</span>
                </div>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-bold">2</span>
              <div>
                <p className="font-semibold">Scroll down and tap "Add to Home Screen"</p>
                <p className="text-muted-foreground mt-1">Look for the + icon</p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-bold">3</span>
              <div>
                <p className="font-semibold">Tap "Add" in the top right</p>
                <p className="text-muted-foreground mt-1">The app will appear on your home screen</p>
              </div>
            </li>
          </ol>

          <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
            <p className="text-sm text-amber-400 font-semibold">⚠️ Important for iOS (iPhone/iPad) users</p>
            <p className="text-sm text-muted-foreground mt-1">
              Login may not work properly in Safari browser. Please install as PWA app first before logging in.
            </p>
          </div>
        </div>
      );
    }

    if (platform === "android") {
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
            <Smartphone className="w-6 h-6 text-green-400" />
            <div>
              <p className="font-semibold text-green-400">Android Installation</p>
              <p className="text-sm text-muted-foreground">Install as app for best experience</p>
            </div>
          </div>
          
          <ol className="space-y-3 text-sm">
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-bold">1</span>
              <div>
                <p className="font-semibold">Tap the menu (⋮) in Chrome</p>
                <p className="text-muted-foreground mt-1">Top right corner of the browser</p>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-bold">2</span>
              <div>
                <p className="font-semibold">Tap "Add to Home screen" or "Install app"</p>
                <div className="flex items-center gap-2 mt-1 text-muted-foreground">
                  <Download className="w-4 h-4" />
                  <span>Look for this option</span>
                </div>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-bold">3</span>
              <div>
                <p className="font-semibold">Tap "Install" or "Add"</p>
                <p className="text-muted-foreground mt-1">The app will appear on your home screen</p>
              </div>
            </li>
          </ol>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
          <Monitor className="w-6 h-6 text-purple-400" />
          <div>
            <p className="font-semibold text-purple-400">Desktop Installation</p>
            <p className="text-sm text-muted-foreground">Install for quick access</p>
          </div>
        </div>
        
        <ol className="space-y-3 text-sm">
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-bold">1</span>
            <div>
              <p className="font-semibold">Look for the install icon in the address bar</p>
              <div className="flex items-center gap-2 mt-1 text-muted-foreground">
                <Download className="w-4 h-4" />
                <span>Usually on the right side</span>
              </div>
            </div>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-bold">2</span>
            <div>
              <p className="font-semibold">Click "Install" when prompted</p>
              <p className="text-muted-foreground mt-1">The app will open in its own window</p>
            </div>
          </li>
        </ol>
      </div>
    );
  };

  const steps: TutorialStep[] = [
    {
      title: isAuthenticated && user ? `Welcome back, ${user.name || "User"}!` : "Welcome to Wega di Number Korsou (AI Prediction)!",
      description: "Your AI-powered lottery prediction assistant",
      content: (
        <div className="space-y-4 text-center">
          {/* User Profile Picture */}
          {isAuthenticated && user && (
            <div className="flex justify-center mb-4">
              {user.profilePicture ? (
                <img
                  src={user.profilePicture}
                  alt={user.name || "Profile"}
                  className="w-20 h-20 rounded-full border-4 border-primary/40 object-cover"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary to-purple-500 flex items-center justify-center border-4 border-primary/40">
                  <User className="w-10 h-10 text-white" />
                </div>
              )}
            </div>
          )}
          
          <div className="text-6xl">🎰</div>
          <p className="text-lg">
            Get AI-powered predictions based on historical lottery data analysis
          </p>
          <p className="text-muted-foreground">
            This quick tutorial will show you how to install the app and use its features
          </p>
          <div className="mt-6 pt-4 border-t border-border">
            <p className="text-sm font-semibold text-foreground">Created by Cyrus Brands</p>
            <p className="text-xs text-muted-foreground mt-2">
              Need help? Contact us:
            </p>
            <p className="text-sm font-medium text-primary mt-1">
              📞 +5999 512-2377 (Call or WhatsApp)
            </p>
          </div>
        </div>
      ),
    },
    {
      title: "Install as App (PWA)",
      description: "For the best experience, install this app on your device",
      content: getPWAInstallContent(),
    },
    {
      title: "Generate Predictions",
      description: "Get AI-powered number predictions",
      content: (
        <div className="space-y-4">
          <p>Navigate to the <strong>Predict</strong> page to:</p>
          <ul className="space-y-2 text-sm">
            <li className="flex gap-2">
              <span className="text-primary">✓</span>
              <span>Choose digit type (2, 3, or 4 digits)</span>
            </li>
            <li className="flex gap-2">
              <span className="text-primary">✓</span>
              <span>Select how many predictions you want (1-50)</span>
            </li>
            <li className="flex gap-2">
              <span className="text-primary">✓</span>
              <span>Get AI-generated predictions with confidence scores</span>
            </li>
            <li className="flex gap-2">
              <span className="text-primary">✓</span>
              <span>See which AI methods contributed to each prediction</span>
            </li>
          </ul>
        </div>
      ),
    },
    {
      title: "View Statistics & History",
      description: "Analyze patterns and trends",
      content: (
        <div className="space-y-4">
          <p><strong>Statistics Page:</strong></p>
          <ul className="space-y-2 text-sm mb-4">
            <li className="flex gap-2">
              <span className="text-primary">✓</span>
              <span>View hot and cold numbers</span>
            </li>
            <li className="flex gap-2">
              <span className="text-primary">✓</span>
              <span>See digit frequency analysis</span>
            </li>
            <li className="flex gap-2">
              <span className="text-primary">✓</span>
              <span>Search for specific numbers in history</span>
            </li>
          </ul>
          <p><strong>History Page:</strong></p>
          <ul className="space-y-2 text-sm">
            <li className="flex gap-2">
              <span className="text-primary">✓</span>
              <span>Browse all past lottery draws</span>
            </li>
            <li className="flex gap-2">
              <span className="text-primary">✓</span>
              <span>Filter by date and prize type</span>
            </li>
          </ul>
        </div>
      ),
    },
    {
      title: "Save Your Favorite Numbers",
      description: "Track numbers you want to play",
      content: (
        <div className="space-y-4">
          <p>Use the <strong>Saved Numbers</strong> feature to:</p>
          <ul className="space-y-2 text-sm">
            <li className="flex gap-2">
              <span className="text-primary">✓</span>
              <span>Save your favorite lucky numbers</span>
            </li>
            <li className="flex gap-2">
              <span className="text-primary">✓</span>
              <span>Organize numbers by digit type</span>
            </li>
            <li className="flex gap-2">
              <span className="text-primary">✓</span>
              <span>Quick access to numbers you play regularly</span>
            </li>
          </ul>
          <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg mt-4">
            <p className="text-sm text-blue-400 font-semibold">💡 Pro Tip</p>
            <p className="text-sm text-muted-foreground mt-1">
              Login to save your predictions and favorite numbers across devices
            </p>
          </div>
        </div>
      ),
    },
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    setIsOpenState(false);
  };

  const handleComplete = () => {
    // Just close tutorial (will reappear on next visit for persistent onboarding)
    setIsOpenState(false);
  };

  return (
    <>
      <OnboardingContext.Provider value={{ openTutorial }}>
        {children}
      </OnboardingContext.Provider>
      {isOpenState && <TutorialModal />}
    </>
  );

  function TutorialModal() {

  const currentStepData = steps[currentStep];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-md p-4">
      <Card className="w-full max-w-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-2 border-primary/40 shadow-[0_20px_60px_rgba(0,0,0,0.8),0_0_40px_rgba(59,130,246,0.3)] transform-gpu">
        <CardHeader className="relative">
          <button
            onClick={handleSkip}
            className="absolute top-4 right-4 p-2 hover:bg-secondary rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <CardTitle className="text-2xl bg-gradient-to-r from-primary via-amber-300 to-primary bg-clip-text text-transparent">
            {currentStepData.title}
          </CardTitle>
          <CardDescription className="text-base">
            {currentStepData.description}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Content */}
          <div className="min-h-[300px]">
            {currentStepData.content}
          </div>

          {/* Progress Indicator */}
          <div className="flex items-center justify-center gap-2">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`h-2 rounded-full transition-all ${
                  index === currentStep
                    ? "w-8 bg-primary"
                    : index < currentStep
                    ? "w-2 bg-primary/50"
                    : "w-2 bg-secondary"
                }`}
              />
            ))}
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between gap-4">
            <Button
              variant="outline"
              onClick={handlePrevious}
              disabled={currentStep === 0}
              className="gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </Button>

            <div className="text-sm text-muted-foreground">
              Step {currentStep + 1} of {steps.length}
            </div>

            {currentStep < steps.length - 1 ? (
              <Button onClick={handleNext} className="gap-2">
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button onClick={handleComplete} className="gap-2">
                Get Started
                <ChevronRight className="w-4 h-4" />
              </Button>
            )}
          </div>

          {/* Skip Button */}
          <div className="text-center">
            <button
              onClick={handleSkip}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors underline"
            >
              Skip tutorial
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
  }
}
